import { createContext, useState, useContext, useEffect, useRef } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import {
  FaCheck,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimes
} from 'react-icons/fa'

interface ModalProps {
  title: string
  message?: string | JSX.Element
  options?: {
    size?: 'sm' | 'lg' | 'xl'
    onConfirm?: () => void
    onCancel?: () => void
    cancelText?: string
    backdropClassName?: string
    zIndex?: number
  }
}

interface ThemeContext {
  error: (data: ModalProps) => void
  warning: (data: ModalProps) => void
  info: (data: ModalProps) => void
  show: (data: ModalProps) => void
  requestPassword: (onConfirm: () => any) => void
}

const Theme = createContext<ThemeContext>({} as ThemeContext)

export const ModalProvider: React.FC = ({ children }) => {
  const [modalProps, setModalProps] = useState({} as ModalProps)
  const [modalOpen, setModalOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [theme, setTheme] = useState<
    'error' | 'warning' | 'info' | 'show' | 'password' | null
  >(null)

  // O modal continua na tela durante a animação de fechamento, então o botão
  // segue clicável depois do primeiro clique. Sem essa trava síncrona, um duplo
  // clique dispara o onConfirm duas vezes (ex.: pagar a mesma operação 2x).
  const confirmingRef = useRef(false)

  // SUP-1000: a limpeza dos props espera a animação de fechamento terminar, mas o
  // timer não era cancelado. Se outro modal abrisse dentro dos 300ms, o timer do
  // anterior disparava e apagava o conteúdo do modal novo, já na tela — ele ficava
  // sem título, sem texto e com "OK" no lugar de "Sim"/"Cancelar". Acontecia no
  // Serasa: fechar o "Deseja mesmo consultar?" e receber o 409 logo em seguida.
  const clearPropsTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (!modalOpen) {
      clearPropsTimer.current = setTimeout(() => {
        setModalProps({} as ModalProps)
      }, 300)
    }

    return () => {
      if (clearPropsTimer.current) {
        clearTimeout(clearPropsTimer.current)
      }
    }
  }, [modalOpen])

  const open = (
    nextTheme: 'error' | 'warning' | 'info' | 'show' | 'password',
    data: ModalProps
  ) => {
    confirmingRef.current = false
    setConfirming(false)
    setTheme(nextTheme)
    setModalProps(data)
    setModalOpen(true)
  }

  const error = (data: ModalProps) => {
    open('error', data)
  }

  const warning = (data: ModalProps) => {
    open('warning', data)
  }

  const info = (data: ModalProps) => {
    open('info', data)
  }

  const show = (data: ModalProps) => {
    open('show', data)
  }

  const requestPassword = (onConfirm: () => any) => {
    open('password', {
      title: 'Senha Requisitada',
      message: 'Para completar esta ação é necessário informar sua senha',
      options: {
        onConfirm,
        onCancel: () => {
          //
        }
      }
    })
  }

  const confirm = () => {
    if (confirmingRef.current) {
      return
    }

    if (modalProps?.options?.onConfirm) {
      confirmingRef.current = true
      setConfirming(true)

      modalProps.options.onConfirm()
    }

    setModalOpen(false)
  }

  const dimiss = () => {
    if (modalProps?.options?.onCancel) {
      modalProps?.options?.onCancel()
    }
    setModalOpen(false)
  }

  return (
    <Theme.Provider value={{ error, warning, info, show, requestPassword }}>
      {children}
      <Modal
        show={modalOpen}
        onHide={dimiss}
        centered
        size={modalProps?.options?.size || 'sm'}
        backdropClassName={modalProps?.options?.backdropClassName || 'backdrop-top'}
        style={{ zIndex: modalProps?.options?.zIndex || 1070 }}
      >
        <Modal.Body className="text-center">
          <div className="d-flex justify-content-end">
            <button type="button" className="text-muted" onClick={dimiss}>
              <FaTimes />
            </button>
          </div>
          {theme === 'info' && (
            <FaInfoCircle className="text-primary mb-3" size="3rem" />
          )}
          {theme === 'warning' && (
            <FaExclamationTriangle className="text-warning mb-3" size="3rem" />
          )}
          {theme === 'error' && (
            <FaInfoCircle className="text-danger mb-3" size="3rem" />
          )}
          <h5>{modalProps?.title}</h5>
          {modalProps?.message && (
            <div className="mb-3">
              {typeof modalProps?.message === 'string' && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: `<p>${modalProps?.message as string}</p>`
                  }}
                />
              )}
              {typeof modalProps?.message !== 'string' && modalProps?.message}
            </div>
          )}
          {theme === 'password' && (
            <Form.Control
              placeholder="Senha"
              type="password"
              className="mb-3 mt-3"
            />
          )}
          <div className="d-flex justify-content-center">
            <Button
              type="button"
              variant="outline-primary"
              disabled={confirming}
              onClick={confirm}
            >
              <FaCheck />{' '}
              {modalProps?.options?.onConfirm && theme !== 'password'
                ? 'Sim'
                : 'OK'}
            </Button>
            {modalProps?.options?.onCancel && (
              <Button
                onClick={dimiss}
                className="ml-2"
                variant="outline-secondary"
                type="button"
              >
                <FaTimes /> {modalProps?.options?.cancelText || 'Cancelar'}
              </Button>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </Theme.Provider>
  )
}

export const useModal = (): ThemeContext => {
  const context = useContext(Theme)

  if (!context) {
    throw new Error('The hook useModal must be used within an ModalProvider')
  }

  return context
}
